import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import TeacherSmartCard from './TeacherSmartCard';
import TeacherToolGrid, { GROUPS } from './TeacherToolGrid';
import TeacherAttendanceCard from './TeacherAttendanceCard';
import TeacherPaperHub from './TeacherPaperHub';
import TeacherDispatchCard from './TeacherDispatchCard';
import TeacherProfileCard from './TeacherProfileCard';
import TeacherStudentAccess from './TeacherStudentAccess';
import { StudentsView, TimetableView } from './TeacherNativeViews';
import TeacherLiveGrid from './LiveStream/TeacherLiveGrid';
import ComplianceAlertModal from './ComplianceAlertModal';
import { getLiveSocket } from '../utils/liveSocket';
import PeerFeedbackForm from './PeerFeedbackForm';
import './TeacherHome.css';

const pad = (n) => String(n).padStart(2, '0');
const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const TABS = [
  { id: 'home', label: 'Home', icon: '🏠' },
  { id: 'attendance', label: 'Attendance', icon: '📋' },
  { id: 'papers', label: 'Papers', icon: '📄' },
  { id: 'profile', label: 'Profile', icon: '👤' }
];
const ALL_TOOLS = GROUPS.flatMap((g) => g.tools);

function TeacherHome({
  user, onLogout, isTeacherAdmin, isImpersonating, onStopImpersonation,
  legacyOpen, legacyTitle, onCloseLegacy, onOpenSection, onChangePassword, onViewAsStudent, children
}) {
  const today = toYmd(new Date());
  console.log('[TeacherHome] role:', user.role, '| isTeacherAdmin:', isTeacherAdmin);
  const [tab, setTab] = useState('home');
  const [view, setView] = useState(null); // native sub-page: homework | timetable | students | notices
  const [classes, setClasses] = useState([]);
  const [cls, setCls] = useState(user.class || '');
  const [students, setStudents] = useState([]);
  const [records, setRecords] = useState([]);
  const [showPeerForm, setShowPeerForm] = useState(false);
  const [isLiveClassActive, setIsLiveClassActive] = useState(false);

  // Track live-class state so the compliance pop-up never interrupts a running class.
  useEffect(() => {
    const headers = { 'x-user-id': String(user?._id || user?.id || '') };
    const check = () => axios.get(`${API_BASE_URL}/api/livestream/active-streams`, { headers, timeout: 15000 })
      .then((r) => setIsLiveClassActive(Array.isArray(r.data) && r.data.length > 0)).catch(() => {});
    check();
    const timer = setInterval(check, 30000);
    const socket = getLiveSocket();
    const onStart = () => setIsLiveClassActive(true);
    socket.on('liveStreamStarted', onStart);
    socket.on('liveStreamStopped', check);
    return () => { clearInterval(timer); socket.off('liveStreamStarted', onStart); socket.off('liveStreamStopped', check); };
  }, [user]);

  useEffect(() => {
    if (user.class) return;
    axios.get(`${API_BASE_URL}/api/classes`).then((r) => {
      setClasses(r.data || []);
      if (r.data?.[0]) setCls((c) => c || r.data[0].name);
    }).catch(() => {});
  }, [user.class]);

  const loadRecords = useCallback(() => {
    if (!cls) return;
    axios.get(`${API_BASE_URL}/api/attendance-records/${encodeURIComponent(cls)}`)
      .then((r) => setRecords((r.data || []).filter((x) => x.date === today))).catch(() => setRecords([]));
  }, [cls, today]);

  useEffect(() => {
    if (!cls) return;
    axios.get(`${API_BASE_URL}/api/users`, { params: { role: 'student', class: cls } })
      .then((r) => setStudents(r.data || [])).catch(() => setStudents([]));
    loadRecords();
  }, [cls, loadRecords]);

  const ids = new Set(students.map((s) => s._id));
  const todays = records.filter((r) => ids.has(r.studentId));
  const marked = students.length > 0 && todays.length >= students.length;
  const present = todays.filter((r) => r.status !== 'absent').length;

  const goTab = (id) => { setTab(id); setView(null); if (legacyOpen) onCloseLegacy(); window.scrollTo(0, 0); };
  const pick = (t) => {
    window.scrollTo(0, 0);
    if (t.kind === 'tab') { setTab(t.id); setView(null); }
    else if (t.kind === 'view') { setTab('home'); setView(t.id); }
    else onOpenSection(t.id);
  };
  const back = () => { if (legacyOpen) onCloseLegacy(); setView(null); };

  const sub = legacyOpen || view;
  const subId = legacyOpen ? legacyTitle : view;
  const subTitle = ALL_TOOLS.find((t) => t.id === subId)?.label || 'Details';
  const showHome = tab === 'home' && !sub;
  const needPicker = !user.class && !legacyOpen && classes.length > 0;

  return (
    <div className="th-root">
      {showHome ? (
        <TeacherSmartCard user={user} cls={cls} present={present} total={students.length} marked={marked} onMark={() => goTab('attendance')} />
      ) : (
        <header className="th-topbar">
          <div className="th-topbar-main">
            <div className="th-name">{sub ? subTitle : TABS.find((t) => t.id === tab).label}</div>
            <div className="th-sub">{user.name}{cls ? ` · Class ${cls}` : ''}</div>
          </div>
        </header>
      )}

      {isImpersonating && (
        <div className="th-banner">Viewing as teacher. {onStopImpersonation && <button className="th-link" onClick={onStopImpersonation}>Exit</button>}</div>
      )}

      <main className="th-content">
        {sub && <button className="th-back" onClick={back}>← Back</button>}
        {needPicker && (
          <select className="th-input" value={cls} onChange={(e) => setCls(e.target.value)} aria-label="Class">
            {classes.map((c) => <option key={c._id} value={c.name}>Class {c.name}{c.section ? ` - ${c.section}` : ''}</option>)}
          </select>
        )}

        {legacyOpen && <div className="th-legacy">{children}</div>}
        {!legacyOpen && view === 'students' && <StudentsView students={students} />}
        {!legacyOpen && view === 'access' && <TeacherStudentAccess students={students} onViewAsStudent={onViewAsStudent} />}
        {!legacyOpen && view === 'timetable' && <TimetableView cls={cls} user={user} />}
        {!legacyOpen && view === 'homework' && <TeacherDispatchCard user={user} cls={cls} initialKind="homework" />}
        {!legacyOpen && view === 'notices' && <TeacherDispatchCard user={user} cls={cls} initialKind="notice" />}

        {showHome && (
          <section id="teacher-live-section" aria-label="Live Classroom" className="rounded-2xl bg-slate-900 p-3">
            {/* VIEW-ONLY: no AdminStreamControl, no URL input, no ON/OFF toggles */}
            <TeacherLiveGrid />
          </section>
        )}
        {showHome && <TeacherToolGrid onPick={pick} isTeacherAdmin={isTeacherAdmin} />}
        {!sub && tab === 'attendance' && (
          <TeacherAttendanceCard user={user} cls={cls} students={students} todayRecords={records} today={today} onSaved={loadRecords} />
        )}
        {!sub && tab === 'papers' && <TeacherPaperHub user={user} cls={cls} academicYear="2026-2027" />}
        {!sub && tab === 'profile' && (
          <TeacherProfileCard user={user} cls={cls} onDailyAttendanceHW={() => onOpenSection('dailyhw')} onTakeAttendance={() => goTab('attendance')} onMasterAttendance={() => onOpenSection('attendancemaster')} onMyAttendance={() => onOpenSection('myattendance')} onChangePassword={onChangePassword} onLogout={onLogout} />
        )}
      </main>

      {!isImpersonating && showHome && <ComplianceAlertModal user={user} isLiveClassActive={isLiveClassActive} />}
      {showPeerForm && <PeerFeedbackForm user={user} onClose={() => setShowPeerForm(false)} />}

      <nav className="th-tabbar" aria-label="Main">
        {TABS.map((t) => (
          <button key={t.id} className={`th-tab ${!sub && tab === t.id ? 'th-tab-on' : ''}`} onClick={() => goTab(t.id)}>
            <span aria-hidden="true">{t.icon}</span><span>{t.label}</span>
          </button>
        ))}
        <button className="th-tab" onClick={() => setShowPeerForm(true)} title="सहकर्मी फ़ीडबैक">
          <span aria-hidden="true">🛡️</span><span>Peer Feedback</span>
        </button>

      </nav>
    </div>
  );
}

export default TeacherHome;
